export const AI_MAPPING_TASK_POLL_INTERVAL_MS = 3000;

export const AI_MAPPING_TASK_MAX_POLLS = 600;

export interface AiMappingCategoryCount {
  category: string;
  count: number;
}

export interface AiMappingTaskStatusLike {
  status: string;
  progress?: number | null;
  progressMessage?: string | null;
  error?: string | null;
  counts?: AiMappingCategoryCount[] | null;
  pdfUrl?: string | null;
}

export type AiMappingTaskPhase = "idle" | "pending" | "progress" | "success" | "failure";

export interface AiMappingTaskSnapshot {
  phase: AiMappingTaskPhase;
  status: string | null;
  progress: number | null;
  progressMessage: string | null;
  error: string | null;
  counts: AiMappingCategoryCount[] | null;
  pdfUrl: string | null;
  isTerminal: boolean;
  isIndeterminate: boolean;
}

export const initialAiMappingTaskSnapshot: AiMappingTaskSnapshot = {
  phase: "idle",
  status: null,
  progress: null,
  progressMessage: null,
  error: null,
  counts: null,
  pdfUrl: null,
  isTerminal: false,
  isIndeterminate: true,
};

export const isTerminalTaskStatus = (status: string): boolean =>
  status === "SUCCESS" || status === "FAILURE";

const clampProgress = (progress: number | null | undefined): number | null => {
  if (typeof progress !== "number" || !Number.isFinite(progress)) return null;
  return Math.min(100, Math.max(0, Math.round(progress)));
};

export function reduceAiMappingTaskStatus(
  snapshot: AiMappingTaskSnapshot,
  status: AiMappingTaskStatusLike
): AiMappingTaskSnapshot {
  const raw = status.status;

  if (raw === "SUCCESS") {
    return {
      phase: "success",
      status: raw,
      progress: 100,
      progressMessage: status.progressMessage ?? null,
      error: null,
      counts: status.counts ?? null,
      pdfUrl: status.pdfUrl ?? null,
      isTerminal: true,
      isIndeterminate: false,
    };
  }

  if (raw === "FAILURE") {
    return {
      phase: "failure",
      status: raw,
      progress: snapshot.progress,
      progressMessage: null,
      error: status.error || "Detection failed",
      counts: null,
      pdfUrl: null,
      isTerminal: true,
      isIndeterminate: false,
    };
  }

  if (raw === "PROGRESS") {
    return {
      phase: "progress",
      status: raw,
      progress: clampProgress(status.progress) ?? snapshot.progress,
      progressMessage: status.progressMessage ?? null,
      error: null,
      counts: null,
      pdfUrl: null,
      isTerminal: false,
      isIndeterminate: false,
    };
  }

  return {
    ...snapshot,
    phase: snapshot.phase === "idle" ? "pending" : snapshot.phase,
    status: raw,
    isTerminal: false,
    isIndeterminate: snapshot.phase === "idle" || snapshot.phase === "pending",
  };
}
